import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import ConfirmDialog from "../../../../components/common/ConfirmDialog";
import api from "../../../../api/client";

const STATUS_TABS = ["All", "draft", "active", "archived"];

const STATUS_LABEL = { draft: "Draft", active: "Active", archived: "Archived" };
const STATUS_COLOR = {
  draft: { background: "#fff7ed", color: "#c2410c", border: "#fed7aa" },
  active: { background: "#f0fdf4", color: "#15803d", border: "#bbf7d0" },
  archived: { background: "#f8fafc", color: "#64748b", border: "#e2e8f0" },
};

export default function WorkflowList() {
  const navigate = useNavigate();
  const [workflows, setWorkflows] = useState([]);
  const [activeStatus, setActiveStatus] = useState("All");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const menuRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await api.get("/manufacturing/workflows");
      setWorkflows(resp.data.data || []);
    } catch (err) {
      console.error("Failed to load workflows", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const filtered = useMemo(() => {
    let list = workflows;
    if (activeStatus !== "All") list = list.filter((w) => w.status === activeStatus);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter((w) => w.name.toLowerCase().includes(q));
    }
    return list;
  }, [workflows, activeStatus, search]);

  const counts = useMemo(() => {
    const map = { All: workflows.length, draft: 0, active: 0, archived: 0 };
    workflows.forEach((w) => { if (map[w.status] !== undefined) map[w.status]++; });
    return map;
  }, [workflows]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/manufacturing/workflows/${id}/status`, { status });
      load();
    } catch (err) {
      alert(err.response?.data?.error || "Status update failed");
    }
  };

  const doDelete = async (id) => {
    try {
      await api.delete(`/manufacturing/workflows/${id}`);
      load();
    } catch (err) {
      alert(err.response?.data?.error || "Delete failed");
    }
  };

  return (
    <div style={{ padding: 20, height: "100%", overflow: "auto", background: "#f5f8fc" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
        <h1 style={{ margin: 0, color: "#0f2d5c" }}>Workflow Templates</h1>
        <button
          className="btn-primary"
          type="button"
          onClick={() => navigate("/manufacturing/settings/workflows/new")}
        >
          + Create Workflow
        </button>
      </div>

      <div style={{ marginTop: 14, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
        {STATUS_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setActiveStatus(tab)}
            style={{
              border: "1px solid #d6dfed",
              background: tab === activeStatus ? "#0f62fe" : "#fff",
              color: tab === activeStatus ? "#fff" : "#1f2937",
              borderRadius: 999,
              padding: "7px 14px",
              cursor: "pointer",
              fontWeight: 600,
              fontSize: 13,
            }}
          >
            {STATUS_LABEL[tab] || tab} ({counts[tab] || 0})
          </button>
        ))}
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search workflows..."
          style={{
            marginLeft: "auto",
            minWidth: 240,
            padding: "8px 12px",
            border: "1px solid #d6dfed",
            borderRadius: 8,
            fontSize: 13,
          }}
        />
      </div>

      <div style={{ marginTop: 16, display: "grid", gap: 12 }}>
        {loading && <div style={{ color: "#64748b" }}>Loading workflows...</div>}
        {!loading && filtered.length === 0 && (
          <div style={{ background: "#fff", border: "1px solid #e4ebf5", borderRadius: 12, padding: 24, color: "#64748b" }}>
            No workflows found.
          </div>
        )}
        {!loading && filtered.map((w) => {
          const badge = STATUS_COLOR[w.status] || STATUS_COLOR.draft;
          return (
            <div
              key={w.id}
              style={{
                background: "#fff",
                border: "1px solid #e4ebf5",
                borderRadius: 12,
                padding: "16px 20px",
                display: "flex",
                alignItems: "center",
                gap: 16,
              }}
            >
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
                  <span style={{ fontWeight: 700, fontSize: 15, color: "#1f2937" }}>{w.name}</span>
                  <span
                    style={{
                      ...badge,
                      borderRadius: 999,
                      padding: "2px 10px",
                      fontSize: 12,
                      fontWeight: 700,
                      border: `1px solid ${badge.border}`,
                    }}
                  >
                    {STATUS_LABEL[w.status] || w.status}
                  </span>
                  <span style={{ fontSize: 12, color: "#94a3b8", background: "#f1f5f9", borderRadius: 999, padding: "2px 8px" }}>
                    v{w.version}
                  </span>
                </div>
                <div style={{ marginTop: 6, display: "flex", gap: 16, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 12, color: "#64748b" }}>
                    {w.stages?.length || 0} stage{w.stages?.length !== 1 ? "s" : ""}
                  </span>
                  <span style={{ fontSize: 12, color: "#64748b" }}>
                    Updated {new Date(w.updatedAt).toLocaleDateString()}
                  </span>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center", flexShrink: 0 }}>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ fontSize: 13, padding: "6px 14px" }}
                  onClick={() => navigate(`/manufacturing/settings/workflows/${w.id}`)}
                >
                  Edit
                </button>
                <div style={{ position: "relative" }} ref={menuOpen === w.id ? menuRef : null}>
                  <button
                    type="button"
                    style={{
                      border: "1px solid #d6dfed",
                      background: "#fff",
                      borderRadius: 8,
                      padding: "6px 10px",
                      cursor: "pointer",
                      fontSize: 16,
                      lineHeight: 1,
                    }}
                    onClick={() => setMenuOpen(menuOpen === w.id ? null : w.id)}
                  >
                    ⋮
                  </button>
                  {menuOpen === w.id && (
                    <div
                      style={{
                        position: "absolute",
                        right: 0,
                        top: "calc(100% + 4px)",
                        background: "#fff",
                        border: "1px solid #d8e3f3",
                        borderRadius: 10,
                        boxShadow: "0 8px 24px rgba(11,33,72,0.18)",
                        zIndex: 100,
                        minWidth: 160,
                        overflow: "hidden",
                      }}
                    >
                      {w.status !== "active" && (
                        <button
                          type="button"
                          className="ts-dropdown-item"
                          onClick={() => { setMenuOpen(null); setStatus(w.id, "active"); }}
                        >
                          Set Active
                        </button>
                      )}
                      {w.status !== "draft" && (
                        <button
                          type="button"
                          className="ts-dropdown-item"
                          onClick={() => { setMenuOpen(null); setStatus(w.id, "draft"); }}
                        >
                          Set Draft
                        </button>
                      )}
                      {w.status !== "archived" && (
                        <button
                          type="button"
                          className="ts-dropdown-item"
                          onClick={() => { setMenuOpen(null); setStatus(w.id, "archived"); }}
                        >
                          Archive
                        </button>
                      )}
                      <button
                        type="button"
                        className="ts-dropdown-item danger"
                        onClick={() => { setMenuOpen(null); setDeleteConfirm(w); }}
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {deleteConfirm && (
        <ConfirmDialog
          title="Delete Workflow"
          message={`Delete "${deleteConfirm.name}"? This cannot be undone.`}
          confirmLabel="Delete"
          confirmClass="btn-danger"
          onConfirm={() => { const id = deleteConfirm.id; setDeleteConfirm(null); doDelete(id); }}
          onClose={() => setDeleteConfirm(null)}
        />
      )}
    </div>
  );
}
